import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import MentionEditorAdd from '../tasks/MentionEditorAdd';
import DateInput from '../components/Forms/DateInput';
import axiosInstance from '../../services/AxiosInstance';
import '../tasks/mention.css';
import '../tasks/tasklist.css';
import '../tasks/addtask.css';
import '../Documents/commonstyle.css';
import './forum.css';

const customStyles = {
  control: (base, state) => ({
    ...base,
    borderRadius: '10px',
    borderColor: state.isFocused ? '#198754' : 'rgba(0, 0, 0, 0.125)',
    boxShadow: 'none',
    '&:hover': {
      borderColor: '#198754',
    },
  }),
  menu: (base) => ({
    ...base,
    borderRadius: '10px',
    zIndex: 9999,
  }),
};

const fetchUsersByFilters = async (branchIds, departmentIds, subDepartmentIds) => {
  try {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    if (!userData?.corporateId) return [];

    const params = new URLSearchParams();
    params.append('corporateId', parseInt(userData.corporateId, 10));
    if (branchIds?.length) params.append('branch', branchIds.join(','));
    if (departmentIds?.length) params.append('department', departmentIds.join(','));
    if (subDepartmentIds?.length) params.append('sub_department', subDepartmentIds.join(','));

    const response = await axiosInstance.get(`/list/corporateuserslist?${params.toString()}`);
    const users = response.data.users || [];
    return users.filter((user) => user.id !== userData.id);
  } catch (error) {
    console.error('Error fetching users by filters:', error);
    return [];
  }
};

const ForumForm = ({
  onSubmit,
  onCancel,
  users = [],
  branches = [],
  departments = [],
  subDepartments = [],
  submitting = false,
  initialValues = null,
  existingAttachments = [],
  submitLabel = 'Save',
}) => {
  const [isOpenSettings, setIsOpenSettings] = useState(true);
  const [isOpenDocuments, setIsOpenDocuments] = useState(false);
  const [filteredDepartments, setFilteredDepartments] = useState(departments);
  const [filteredSubDepartments, setFilteredSubDepartments] = useState(subDepartments);
  const [filteredUsers, setFilteredUsers] = useState(users);
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    title: '',
    description: '',
    priority: 'Medium',
    start_date: new Date().toISOString().slice(0, 10),
    email_notification: false,
    is_favorite: false,
    allow_participants_edit: false,
    allow_participants_close: false,
    allow_participants_delete: false,
    branch_ids: [],
    department_ids: [],
    sub_department_ids: [],
    individual_ids: [],
    selectedIndividuals: [],
    files: [],
  });

  useEffect(() => {
    setFilteredDepartments(departments);
  }, [departments]);

  useEffect(() => {
    setFilteredSubDepartments(subDepartments);
  }, [subDepartments]);

  useEffect(() => {
    setFilteredUsers(users);
  }, [users]);

  useEffect(() => {
    if (!initialValues) return;

    setForm((prev) => ({
      ...prev,
      ...initialValues,
      files: initialValues.files || [],
    }));

    if (initialValues.branch_ids?.length) {
      const filtered = departments.filter((dept) => initialValues.branch_ids.includes(dept.branch_id));
      setFilteredDepartments(filtered);
      if (initialValues.department_ids?.length) {
        setFilteredSubDepartments(
          subDepartments.filter((sd) => initialValues.department_ids.includes(sd.department_id))
        );
      }
    } else {
      setFilteredDepartments(departments);
      setFilteredSubDepartments(subDepartments);
    }
  }, [initialValues, departments, subDepartments]);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const syncIndividuals = (selectedUsers) => {
    setField('selectedIndividuals', selectedUsers);
    setField('individual_ids', selectedUsers.map((u) => u.id));
  };

  const handleBranchChange = async (selectedOptions) => {
    const branchIds = selectedOptions ? selectedOptions.map((o) => o.value) : [];

    if (branchIds.length > 0) {
      const filtered = departments.filter((dept) => branchIds.includes(dept.branch_id));
      const validDeptIds = form.department_ids.filter((id) => filtered.some((d) => d.id === id));
      const subs = subDepartments.filter((sd) => validDeptIds.includes(sd.department_id));
      const validSubIds = form.sub_department_ids.filter((id) => subs.some((s) => s.id === id));

      const fetchedUsers = await fetchUsersByFilters(branchIds, validDeptIds, validSubIds);
      const nextUsers = fetchedUsers;

      setFilteredDepartments(filtered);
      setFilteredSubDepartments(subs);
      setFilteredUsers(nextUsers);
      setForm((prev) => ({
        ...prev,
        branch_ids: branchIds,
        department_ids: validDeptIds,
        sub_department_ids: validSubIds,
        selectedIndividuals: prev.selectedIndividuals.filter((u) => nextUsers.some((x) => x.id === u.id)),
        individual_ids: prev.selectedIndividuals
          .filter((u) => nextUsers.some((x) => x.id === u.id))
          .map((u) => u.id),
      }));
    } else {
      setFilteredDepartments(departments);
      setFilteredSubDepartments(subDepartments);
      setFilteredUsers(users);
      setForm((prev) => ({
        ...prev,
        branch_ids: [],
        department_ids: [],
        sub_department_ids: [],
      }));
    }
  };

  const handleDepartmentChange = async (selectedOptions) => {
    const departmentIds = selectedOptions ? selectedOptions.map((o) => o.value) : [];
    const subs = departmentIds.length
      ? subDepartments.filter((sd) => departmentIds.includes(sd.department_id))
      : subDepartments;
    const validSubIds = form.sub_department_ids.filter((id) => subs.some((s) => s.id === id));

    const fetchedUsers = await fetchUsersByFilters(form.branch_ids, departmentIds, validSubIds);
    const nextUsers = fetchedUsers;

    setFilteredSubDepartments(subs);
    setFilteredUsers(nextUsers);
    setForm((prev) => ({
      ...prev,
      department_ids: departmentIds,
      sub_department_ids: validSubIds,
      selectedIndividuals: prev.selectedIndividuals.filter((u) => nextUsers.some((x) => x.id === u.id)),
      individual_ids: prev.selectedIndividuals
        .filter((u) => nextUsers.some((x) => x.id === u.id))
        .map((u) => u.id),
    }));
  };

  const handleSubDepartmentChange = async (selectedOptions) => {
    const subIds = selectedOptions ? selectedOptions.map((o) => o.value) : [];
    const fetchedUsers = await fetchUsersByFilters(form.branch_ids, form.department_ids, subIds);
    const nextUsers = fetchedUsers;

    setFilteredUsers(nextUsers);
    setForm((prev) => ({
      ...prev,
      sub_department_ids: subIds,
      selectedIndividuals: prev.selectedIndividuals.filter((u) => nextUsers.some((x) => x.id === u.id)),
      individual_ids: prev.selectedIndividuals
        .filter((u) => nextUsers.some((x) => x.id === u.id))
        .map((u) => u.id),
    }));
  };

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files || []);
    setField('files', [...form.files, ...newFiles]);
  };

  const handleRemoveFile = (index) => {
    setField('files', form.files.filter((_, i) => i !== index));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(form);
  };

  return (
    <div className="row">
      <div className="col-xl-12 col-xxl-12">
        <div className="card">
          <div className="card-body">
            <section>
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-lg-6 mb-2">
                    <label className="text-label">Title*</label>
                    <input
                      type="text"
                      className="form-control"
                      name="title"
                      autoComplete="off"
                      value={form.title}
                      onChange={(e) => setField('title', e.target.value)}
                      required
                    />
                  </div>
                  <div className="col-lg-6 mb-2">
                    <label className="text-label">Priority</label>
                    <select
                      className="form-control"
                      name="priority"
                      value={form.priority}
                      onChange={(e) => setField('priority', e.target.value)}
                    >
                      <option value="">Select</option>
                      <option>Low</option>
                      <option>Medium</option>
                      <option>High</option>
                    </select>
                  </div>

                  <MentionEditorAdd
                    users={filteredUsers}
                    description={form.description}
                    delta={null}
                    onChange={(data) => setField('description', data.description || '')}
                  />

                  <div className="col-lg-6 mb-2">
                    <label className="text-label">Start Date *</label>
                    <DateInput
                      type="date"
                      className="form-control"
                      name="start_date"
                      value={form.start_date}
                      onChange={(e) => setField('start_date', e.target.value)}
                      required
                    />
                  </div>

                  <div className="w-100" />

                  <div className="col-lg-6 mb-2">
                    <label className="text-label">Branch</label>
                    <Select
                      isMulti
                      styles={customStyles}
                      options={branches.map((branch) => ({
                        value: branch.id,
                        label: branch.name,
                        data: branch,
                      }))}
                      value={form.branch_ids.map((id) => {
                        const branch = branches.find((b) => b.id === id);
                        return branch ? { value: branch.id, label: branch.name, data: branch } : null;
                      }).filter(Boolean)}
                      onChange={handleBranchChange}
                      isClearable
                      placeholder="Select Branches"
                    />
                  </div>

                  <div className="col-lg-6 mb-2">
                    <label className="text-label">Department</label>
                    <Select
                      isMulti
                      styles={customStyles}
                      options={filteredDepartments.map((department) => ({
                        value: department.id,
                        label: `${department.name}${department.branch_name ? ` (${department.branch_name})` : ''}`,
                        data: department,
                      }))}
                      value={form.department_ids.map((id) => {
                        const department = filteredDepartments.find((d) => d.id === id);
                        return department ? { value: department.id, label: department.name, data: department } : null;
                      }).filter(Boolean)}
                      onChange={handleDepartmentChange}
                      isClearable
                      placeholder="Select Departments"
                    />
                  </div>

                  <div className="col-lg-6 mb-2">
                    <label className="text-label">Sub-Department</label>
                    <Select
                      isMulti
                      styles={customStyles}
                      options={filteredSubDepartments.map((sd) => ({
                        value: sd.id,
                        label: sd.name,
                        data: sd,
                      }))}
                      value={form.sub_department_ids.map((id) => {
                        const sd = filteredSubDepartments.find((s) => s.id === id) || subDepartments.find((s) => s.id === id);
                        return sd ? { value: sd.id, label: sd.name, data: sd } : null;
                      }).filter(Boolean)}
                      onChange={handleSubDepartmentChange}
                      isClearable
                      placeholder="Select Sub-Departments"
                    />
                  </div>

                  <div className="col-lg-6 mb-2">
                    <label className="text-label">Individuals</label>
                    <Select
                      isMulti
                      styles={customStyles}
                      options={filteredUsers.map((user) => ({
                        value: user.id,
                        label: `${user.full_name}${(user.department_name || user.branch_name) ? ` (${user.department_name ? `${user.department_name},` : ''}${user.branch_name || ''})` : ''}`,
                        data: user,
                      }))}
                      value={form.selectedIndividuals.map((u) => ({
                        value: u.id,
                        label: u.full_name,
                        data: u,
                      }))}
                      onChange={(selectedOptions) =>
                        syncIndividuals((selectedOptions || []).map((option) => option.data))
                      }
                      placeholder="Select Individuals"
                    />
                  </div>
                </div>

                <div
                  className="card-header accordion mb-0"
                  style={{ cursor: 'pointer', background: '#ffffff', paddingTop: '1rem', paddingLeft: 0 }}
                  onClick={() => setIsOpenSettings((prev) => !prev)}
                  aria-expanded={isOpenSettings}
                >
                  <h4 className="card-title">Settings</h4>
                  <i
                    className="fa fa-chevron-down"
                    style={{
                      transition: 'transform 0.3s',
                      transform: isOpenSettings ? 'rotate(180deg)' : 'rotate(0deg)',
                      fontSize: '1rem',
                      marginLeft: '8px',
                      color: '#888',
                    }}
                    aria-hidden="true"
                  />
                </div>

                {isOpenSettings ? (
                  <div className="row mb-2 pt-2">
                    <div className="col-lg-4 mb-2">
                      <div className="form-check mt-2">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="forumIsFavorite"
                          checked={form.is_favorite}
                          onChange={(e) => setField('is_favorite', e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="forumIsFavorite">
                          Mark as favorite
                        </label>
                      </div>
                    </div>
                    <div className="col-lg-4 mb-2">
                      <div className="form-check mt-2">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="forumEmailNotification"
                          checked={form.email_notification}
                          onChange={(e) => setField('email_notification', e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="forumEmailNotification">
                          Email notifications on comments
                        </label>
                      </div>
                    </div>
                    <div className="col-lg-4 mb-2">
                      <div className="form-check mt-2">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="forumAllowEdit"
                          checked={form.allow_participants_edit}
                          onChange={(e) => setField('allow_participants_edit', e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="forumAllowEdit">
                          Allow participants to edit
                        </label>
                      </div>
                    </div>
                    <div className="col-lg-4 mb-2">
                      <div className="form-check mt-2">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="forumAllowClose"
                          checked={form.allow_participants_close}
                          onChange={(e) => setField('allow_participants_close', e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="forumAllowClose">
                          Allow participants to close forum
                        </label>
                      </div>
                    </div>
                    <div className="col-lg-4 mb-2">
                      <div className="form-check mt-2">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="forumAllowDelete"
                          checked={form.allow_participants_delete}
                          onChange={(e) => setField('allow_participants_delete', e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="forumAllowDelete">
                          Allow participants to delete forum
                        </label>
                      </div>
                    </div>
                  </div>
                ) : null}

                <div
                  className="card-header"
                  style={{ cursor: 'pointer', background: '#ffffff', paddingTop: '1rem', paddingLeft: 0 }}
                  onClick={() => setIsOpenDocuments((prev) => !prev)}
                  aria-expanded={isOpenDocuments}
                >
                  <h4 className="card-title">Documents</h4>
                  <i
                    className="fa fa-chevron-down"
                    style={{
                      transition: 'transform 0.3s',
                      transform: isOpenDocuments ? 'rotate(180deg)' : 'rotate(0deg)',
                      fontSize: '1rem',
                      marginLeft: '8px',
                      color: '#888',
                    }}
                    aria-hidden="true"
                  />
                </div>

                {isOpenDocuments ? (
                  <div className="row pt-2">
                    <div className="form-group mb-3 fileupload-group">
                      <label className="text-label mt-2">Attachments</label>
                      <input
                        ref={fileInputRef}
                        type="file"
                        className="form-control fileupload-control"
                        multiple
                        onChange={handleFileChange}
                      />
                    </div>
                    {form.files.length > 0 && (
                      <div className="mt-3">
                        <label className="text-label">New files to upload:</label>
                        <ul className="list-unstyled">
                          {form.files.map((file, index) => (
                            <li key={`${file.name}-${index}`} className="mb-2 d-flex align-items-center">
                              <span className="me-2">{file.name}</span>
                              <div
                                onClick={() => handleRemoveFile(index)}
                                className="me-2"
                                style={{ cursor: 'pointer', color: '#2A3335' }}
                                title="Remove"
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => e.key === 'Enter' && handleRemoveFile(index)}
                              >
                                <i className="fa fa-trash"></i>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {existingAttachments.length > 0 && (
                      <div className="mt-3">
                        <label className="text-label">Existing attachments:</label>
                        <ul className="list-unstyled">
                          {existingAttachments.map((file) => (
                            <li key={file.id} className="mb-2 d-flex align-items-center">
                              <span className="me-2">{file.file_name}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ) : null}

                <div className="row mt-3">
                  <div className="col-12">
                    <div className="task-form-toolbar text-end toolbar toolbar-bottom">
                      {onCancel && (
                        <button
                          type="button"
                          className="btn btn-cancel task-form-toolbar-btn"
                          onClick={onCancel}
                          disabled={submitting}
                        >
                          Cancel
                        </button>
                      )}
                      <button
                        className="btn btn-primary sw-btn-next task-form-toolbar-btn"
                        type="submit"
                        disabled={submitting}
                      >
                        {submitting ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                            Saving...
                          </>
                        ) : (
                          submitLabel
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForumForm;
